// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a ClickUp task's description, formatted. Pictures pasted into it in ClickUp come as
// `![](https://t….p.clickup-attachments.com/…/image.png)` lines; they load through the media proxy
// addresses the task comes with (`description_images`), as the app loads no web pictures itself.
// A tap on a picture opens the description's pictures in the media viewer (MarkdownDocument's
// openPictures), and a tap on a picture attachment opens the task's pictures there too; other files
// open in the browser.

import type {MarkdownPictureLoader} from '@app/features/document_preview/DocumentPicture';
import {MarkdownDocument} from '@app/features/document_preview/MarkdownDocument';
import type {KonvoTaskAttachment} from '@app/features/konvo_tasks/state/KonvoTasks';
import {openMediaViewerForFiles} from '@app/features/ui/commands/MediaViewerCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {useMemo} from 'react';

export function TaskDescriptionMarkdown({text, images}: {text: string; images: Record<string, string> | undefined}) {
	const loadPicture = useMemo<MarkdownPictureLoader>(() => async (src) => images?.[src] ?? null, [images]);
	return <MarkdownDocument markdown={text} embedded openPictures loadPicture={loadPicture} />;
}

/** Opens a task attachment: pictures in the media viewer (with the task's other pictures), other files in the browser. */
export function openTaskAttachment(attachment: KonvoTaskAttachment, attachments: ReadonlyArray<KonvoTaskAttachment>) {
	if (attachment.preview) {
		const pictures = attachments.filter((entry) => entry.preview);
		void openMediaViewerForFiles(
			pictures.map((entry) => ({
				src: entry.preview!,
				filename: entry.title,
				kind: 'image',
				contentType: entry.mimetype ?? undefined,
				fileSize: entry.size ?? undefined,
			})),
			Math.max(0, pictures.indexOf(attachment)),
		);
		return;
	}
	if (attachment.url) openExternalUrl(attachment.url);
}
